import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  TextField,
} from '@cambridgeassessment/cambridge-ui';
import { Box, Typography } from '@material-ui/core';
import { BusinessStreams } from 'ciam-commons';
import React, { useCallback, useMemo, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import {
  businessUnits,
  COUNTRY_CODE_US,
  REPORT_MAKER_ROLES,
} from 'src/constants';
import useAuth from 'src/hooks/useAuth';
import useOrg from 'src/hooks/useOrg';
import useOrgAppRoles from 'src/hooks/useOrgAppRoles';
import { AppRoleInfo, NewOrgUser, RoleInfo } from 'src/types';
import CancelButtonDialog from '../CancelButtonDialog/CancelButtonDialog';
import { rolesToPayload } from '../OrgUserRoles/utils';
import RolesExpandable from '../OrgUserRolesExpandableView/RolesExpandable';
import RolesSwitch from '../OrgUserRolesExpandableView/RolesSwitch';
import SearchField from '../SearchField/SearchField';
import SharedRoleConfirmationModal from './SharedRoleConfirmationModal';
import { useStyles } from './styles';

//NOSONAR FIXME: no space between asterisk on required Textfiels
//NOSONAR FIXME: no asterisk on required Dropdown
//NOSONAR FIXME: should be solved in CDS-608

//NOSONAR FIXME: issues dropdown on mobile size CDS-609

type Props = {
  onCancel: () => void;
  onSubmit: (data: NewOrgUser, callback: () => void, redirect: boolean) => void;
  isLoading: boolean;
};

type FormErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  roles?: string;
};

export const emailRegExp =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([A-Z\-0-9]+\.)+[A-Z]{2,}))$/i; // NOSONAR

const AddOrgUserForm: React.FC<Props> = ({ onCancel, onSubmit, isLoading }) => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [creditorNumber, setCreditorNumber] = useState<string | undefined>('');
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<
    BusinessStreams[] | undefined
  >([]);
  const [error, setError] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [reportMakerCountryError, setReportMakerCountryError] =
    useState<boolean>(false);

  const { orgRoles } = useOrg();
  const [collapseAll, setCollapseAll] = useState<boolean>(true);
  const [sharedRoles, setSharedRoles] = useState<string[]>([]);
  const [openSharedRoleConfirmationModal, setOpenSharedRoleConfirmationModal] =
    useState<boolean>(false);
  const [saveButtonClicked, setSaveButtonClicked] = useState<boolean>(false);
  const setContinueSaveButtonClicked = useState<boolean>(false)[1];

  const isAssignedRole = (roleName: string) =>
    selectedRoles.indexOf(roleName) >= 0;

  const {
    user: { isCambridgeAssessor, id, country },
  } = useAuth();

  const { filteredOrgAppRoles, searchQuery, updateExpand, updateSearchQuery } =
    useOrgAppRoles(id);

  const handleRemoveBusinessUnits = useCallback(
    (businessUnit) => (): void =>
      setSelectedBusinessUnits((prev) =>
        prev.filter(
          (selectedBusinessUnit) => selectedBusinessUnit !== businessUnit
        )
      ),
    []
  );

  const validateFirstName = useCallback(
    (value) => {
      if (value.trim().length === 0) {
        setError({ ...error, firstName: 'First name is required' });
      } else if (value.length > 128) {
        setError({
          ...error,
          firstName: 'First name should not exceed 128 characters',
        });
      } else {
        setError({
          ...error,
          firstName: '',
        });
      }
    },
    [error]
  );

  const validateLastName = useCallback(
    (value) => {
      if (value.trim().length === 0) {
        setError({ ...error, lastName: 'Last name is required' });
      } else if (value.length > 128) {
        setError({
          ...error,
          lastName: 'Last name should not exceed 128 characters',
        });
      } else {
        setError({
          ...error,
          lastName: '',
        });
      }
    },
    [error]
  );

  const validateEmail = useCallback(
    (value) => {
      if (value.trim().length === 0) {
        setError({ ...error, email: 'Email is required' });
      } else if (!emailRegExp.test(value)) {
        setError({ ...error, email: 'Invalid email address' });
      } else if (value.length > 128) {
        setError({
          ...error,
          email: 'Email should not exceed 128 characters',
        });
      } else {
        setError({
          ...error,
          email: '',
        });
      }
    },
    [error]
  );

  const resetForm = useCallback(() => {
    setError({
      firstName: '',
      lastName: '',
      email: '',
    });
    setFirstName('');
    setLastName('');
    setEmail('');
    setSelectedRoles([]);
    setCreditorNumber('');
    setSelectedBusinessUnits([]);
    setReportMakerCountryError(false);
  }, []);

  const handleSubmit = useCallback(
    async (redirect = false) => {
      if (
        REPORT_MAKER_ROLES.some((role) => selectedRoles.includes(role)) &&
        country !== COUNTRY_CODE_US
      ) {
        setReportMakerCountryError(true);
        return;
      }
      setOpenSharedRoleConfirmationModal(false);
      onSubmit(
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email,
          roles: rolesToPayload(selectedRoles),
          creditorNumber: creditorNumber?.trim() || undefined,
          businessStream: selectedBusinessUnits.length
            ? selectedBusinessUnits
            : undefined,
        },
        resetForm,
        redirect
      );
    },
    [
      creditorNumber,
      email,
      firstName,
      lastName,
      onSubmit,
      resetForm,
      selectedBusinessUnits,
      selectedRoles,
    ]
  );

  const sharedRole = (roleName: string) => {
    return []
      .concat(...filteredOrgAppRoles.appRoles.map((ele) => ele.roles))
      .find((item) => item.isShared && item.name === roleName);
  };

  const getSharedRolesPlatforms = useMemo(() => {
    return filteredOrgAppRoles.appRoles
      .filter((ele) => {
        return ele.roles.some((role) => sharedRoles.includes(role.name));
      })
      .map((appRole) => appRole.platform);
  }, [filteredOrgAppRoles.appRoles, sharedRoles]);

  const handleToggle =
    (roleName: string, key: number) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      if (isChecked && !isAssignedRole(roleName)) {
        setSelectedRoles([...selectedRoles, roleName]);
        if (sharedRole(roleName)) {
          setSharedRoles([...sharedRoles, roleName]);
        }
      }

      if (!isChecked && isAssignedRole(roleName)) {
        setSelectedRoles(selectedRoles.filter((role) => role !== roleName));
        if (sharedRole(roleName)) {
          setSharedRoles(sharedRoles.filter((role) => role !== roleName));
        }
      }

      updateExpand(false, false, key);
    };

  const onCollapseAll = () => {
    setCollapseAll((previousState) => !previousState);
    updateExpand(true, collapseAll, null);
  };

  const openSharedConfirmationModal = (saveButton: boolean) => {
    setOpenSharedRoleConfirmationModal(true);
    if (saveButton) {
      setSaveButtonClicked(true);
    } else {
      setContinueSaveButtonClicked(true);
    }
  };

  const isSubmitDisabled = useMemo(
    () =>
      !firstName ||
      !lastName ||
      !email ||
      !!error.firstName ||
      !!error.lastName ||
      !!error.email ||
      creditorNumber?.length > 64 ||
      isLoading,
    [
      creditorNumber?.length,
      email,
      error.email,
      error.firstName,
      error.lastName,
      firstName,
      isLoading,
      lastName,
    ]
  );

  const classes = useStyles();

  // Fix the undefined orgRoles issue by adding a null check
  const label = (role) => {
    const roleInfo = orgRoles?.roles?.find(
      (roleInfo) => roleInfo.name === role
    );
    return roleInfo?.alias || role;
  };

  return (
    <Box mt={4}>
      <Typography variant="h3" data-testid="form-title">
        User details
      </Typography>
      <Box mt={4} display="flex" flexWrap="wrap">
        <Box flexBasis={354} flexShrink={1} flexGrow={0} mr={[0, 3, 3]} mb={1}>
          <TextField
            required
            label="First or given name "
            validationType={error.firstName ? 'error' : undefined}
            helperText={error.firstName}
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              validateFirstName(e.target.value);
            }}
            data-testid="firstName-input"
          />
        </Box>
        <Box flexBasis={354} flexShrink={1} flexGrow={0} mr={[0, 0, 3]} mb={1}>
          <TextField
            required
            label="Last name or surname "
            validationType={error.lastName ? 'error' : undefined}
            helperText={error.lastName}
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              validateLastName(e.target.value);
            }}
            data-testid="lastName-input"
          />
        </Box>
      </Box>
      <Box display="flex" flexWrap="wrap">
        <Box flexBasis={354} flexShrink={1} flexGrow={0} mr={[0, 0, 3]} mb={1}>
          <TextField
            required
            label="Email address "
            validationType={error.email ? 'error' : undefined}
            helperText={error.email}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              validateEmail(e.target.value);
            }}
            data-testid="email-input"
          />
        </Box>
      </Box>
      {isCambridgeAssessor && (
        <>
          <Box display="flex" flexWrap="wrap">
            <Box
              flexBasis={354}
              flexShrink={1}
              flexGrow={0}
              mr={[0, 0, 3]}
              mb={1}
            >
              <TextField
                label="Creditor number"
                value={creditorNumber}
                onChange={(e) => setCreditorNumber(e.target.value)}
                data-testid="creditorNumber-input"
                validationType={
                  creditorNumber?.trim().length > 64 ? 'error' : null
                }
                helperText={
                  creditorNumber?.trim().length > 64
                    ? 'Maximum 64 characters'
                    : null
                }
              />
            </Box>
          </Box>
          <Box display="flex" flexWrap="wrap">
            <Box flexBasis={354} flexGrow={0} mr={[0, 0, 3]}>
              <Dropdown
                placeholder="Please select one or more business units"
                label="Business unit"
                onChange={(_e, value) =>
                  setSelectedBusinessUnits(value as BusinessStreams[])
                }
                value={selectedBusinessUnits}
                multiple
                className={classes.dropdown}
                data-testid="businessUnit-input"
              >
                {businessUnits.map((businessUnit) => (
                  <DropdownItem key={businessUnit} value={businessUnit}>
                    {businessUnit}
                  </DropdownItem>
                ))}
              </Dropdown>
              {selectedBusinessUnits?.map((businessUnit) => (
                <Chip
                  key={businessUnit}
                  tabIndex={-1}
                  label={businessUnit}
                  className={classes.chip}
                  onDelete={handleRemoveBusinessUnits(businessUnit)}
                />
              ))}
            </Box>
          </Box>
        </>
      )}
      <SharedRoleConfirmationModal
        data-testid="shared-role-notification-modal"
        open={openSharedRoleConfirmationModal}
        title="Add shared role?"
        content={`You have added a shared role. This role is used by multiple services, are you sure you want to continue?`}
        platforms={getSharedRolesPlatforms}
        closeModal={() => setOpenSharedRoleConfirmationModal(false)}
        onSave={() => handleSubmit(saveButtonClicked)}
      />
      <Box display="flex">
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
            }}
          >
            <Typography
              data-testid="role-dropdown-text"
              className={classes.dropdown}
            >
              <Box component="label" className={classes.emphasizedText}>
                <Typography variant="h4" data-testid="user-roles-title">
                  Roles
                </Typography>
              </Box>
            </Typography>
            <Button
              onClick={() => onCollapseAll()}
              data-testid="role-collapse-button"
              style={{ backgroundColor: '#00588f' }}
            >
              {collapseAll ? 'Collapse All' : 'Expand All'}
            </Button>
          </Box>
          <Box mt={1}>
            <SearchField
              searchQuery={searchQuery}
              placeholder="Search for services or roles"
              onSubmit={updateSearchQuery}
              onChange={updateSearchQuery}
            />
          </Box>
          <Box>
            {filteredOrgAppRoles.appRoles.map((appRole: AppRoleInfo, key) => (
              <Box mt={2} key={`accordion-box-${key}`}>
                <RolesExpandable
                  expand={appRole.expand}
                  keyId={key}
                  testid={`${appRole.platform}`}
                  summaryData={appRole.platform}
                  onExpand={updateExpand}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {appRole.roles.length === 0 ? (
                      <Typography
                        style={{ fontSize: '1.15em', lineHeight: '1.51em' }}
                      >
                        No roles for application
                      </Typography>
                    ) : (
                      appRole.roles.map((role: RoleInfo, keyId) => (
                        // eslint-disable-next-line react/jsx-key
                        <RolesSwitch
                          data-testid={`${appRole.platform}-${role.name}-rolesswitch`}
                          keyId={keyId}
                          testid={`${appRole.platform}-${role.name}`}
                          name={role.name}
                          alias={role.alias}
                          isShared={role.isShared}
                          description={role.description}
                          disabled={false}
                          isInternal={role.internal}
                          checked={isAssignedRole(role.name)}
                          onToggle={handleToggle}
                        />
                      ))
                    )}
                  </Box>
                </RolesExpandable>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box mt={4} display="flex" flexWrap="wrap">
        <Box mr={2} mb={2}>
          <Button
            style={{ backgroundColor: '#00588f' }}
            onClick={() =>
              sharedRoles.length > 0
                ? openSharedConfirmationModal(true)
                : handleSubmit(true)
            }
            disabled={isSubmitDisabled}
            data-testid="save-redirect-button"
          >
            Save user
          </Button>
        </Box>
        <Box mb={2} className={classes.secondarySaveButton}>
          <Button
            style={{ color: '#00588f' }}
            variant="outlined"
            onClick={() =>
              sharedRoles.length > 0
                ? openSharedConfirmationModal(false)
                : handleSubmit(false)
            }
            disabled={isSubmitDisabled}
            data-testid="save-button"
          >
            Save and add another
          </Button>
        </Box>
        <Box mr={2} mb={2}>
          <Button
            style={{ color: '#00588f' }}
            variant="text"
            onClick={onCancel}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
        </Box>
      </Box>
      {reportMakerCountryError && (
        <CancelButtonDialog
          open={reportMakerCountryError}
          title={'Report Maker'}
          message={'Sorry, you cannot assign roles related to report maker'}
          onCancel={() => setReportMakerCountryError(false)}
        />
      )}
    </Box>
  );
};

export default hot(AddOrgUserForm);
